<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
  import { useHideHelpBubble } from '@/composition/use-hide-help-bubble';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import PosUiIcon from './PosUiIcon.vue';
  import {
    clampSheetDrag,
    sheetHeightPx,
    snapSheetHeight,
    SHEET_DRAG_THRESHOLD_PX,
  } from './pos-ui-sheet.utils';
  import type { TSheetHeight } from './pos-ui-sheet.utils';

  /**
   * Bottom sheet (plan D31, §11.2): a panel fixed to the bottom of the
   * screen at 40 / 60 / 92 % of the stable window height, with a scrim and a
   * grab handle that snaps between those heights (pulled low, it closes).
   * `role="dialog"`: the focus moves into it on open and back to the opener
   * on close; its 44 px «Cerrar» button (next to the handle), Esc and a
   * scrim tap close it, so it can be dismissed without a scrim to tap or an
   * Esc key (a screen reader, a sheet pulled up to 92 %).
   *
   * Always mounted and toggled with `v-show`, so a parent re-render never
   * destroys a focused field inside it; the dialog semantics exist only while
   * it is open (a hidden `role="dialog"` reads as an open dialog to the
   * page's keyboard shortcuts). `docked` renders the same panel in place (no
   * scrim, no handle, no dialog semantics): one content node can be a side
   * pane on wide screens and a sheet on phones without remounting. The sheet
   * and its scrim end at the docked «Asistente» panel.
   */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const props = withDefaults(
    defineProps<{
      value: boolean;
      /** Accessible name of the dialog. */
      title: string;
      height?: TSheetHeight;
      docked?: boolean;
      /** Sheet only: centres the panel at most this wide (px), e.g. on tablets. */
      maxWidth?: number;
      /**
       * Sheet only: a selector inside the panel that takes the focus when it
       * opens (an input's text is selected); the panel itself otherwise.
       */
      initialFocus?: string;
      /**
       * Sheet only: changing it while open moves the focus in again (the
       * content was swapped while the sheet stayed open).
       */
      focusKey?: string | number;
    }>(),
    {
      height: 'md',
      docked: false,
      maxWidth: undefined,
      initialFocus: undefined,
      focusKey: undefined,
    }
  );
  const emit = defineEmits<{
    (e: 'input', open: boolean): void;
    (e: 'update:height', height: TSheetHeight): void;
  }>();
  const { height: stableHeight } = useStableWindowSize();

  const panel = ref<HTMLElement | null>(null);
  /** The panel height while a drag runs; null otherwise. */
  const dragPx = ref<number | null>(null);
  /** Where the focus was when the sheet opened. */
  let opener: HTMLElement | null = null;
  let drag: {
    pointerId: number;
    startY: number;
    startPx: number;
    moved: boolean;
  } | null = null;

  const shown = computed(() => props.docked || props.value);
  /** An open sheet (not docked): a modal dialog. */
  const isDialog = computed(() => !props.docked && props.value);
  // The help bubble would cover the sheet's bottom buttons.
  useHideHelpBubble(isDialog);
  const panelStyle = computed(() => {
    if (props.docked) return undefined;
    const style: Record<string, string> = {};
    const px = dragPx.value ?? sheetHeightPx(props.height, stableHeight.value);
    if (px > 0) style.height = `${px}px`;
    // Margins, not a transform: a transform would fight the `sheet-in` animation.
    if (props.maxWidth) {
      style.maxWidth = `${props.maxWidth}px`;
      style.marginLeft = 'auto';
      style.marginRight = 'auto';
    }
    return style;
  });

  //#region Focus
  function focusInside(): boolean {
    const active = document.activeElement;
    return (
      !!panel.value && active instanceof Node && panel.value.contains(active)
    );
  }

  /** A focused field must not stay focused inside a hidden panel (the phone keyboard would stay up). */
  function blurInside() {
    const active = document.activeElement;
    if (focusInside() && active instanceof HTMLElement) active.blur();
  }

  function isVisible(element: HTMLElement): boolean {
    return element.getClientRects().length > 0;
  }

  function focusables(): HTMLElement[] {
    if (!panel.value) return [];
    return Array.from(panel.value.querySelectorAll(FOCUSABLE)).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && isVisible(element)
    );
  }

  /** Tab and Shift+Tab stay inside the open sheet. */
  function trapTab(event: KeyboardEvent) {
    const elements = focusables();
    if (elements.length === 0) {
      event.preventDefault();
      panel.value?.focus({ preventScroll: true });
      return;
    }
    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel.value)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event: KeyboardEvent) {
    if (props.docked) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      trapTab(event);
    }
  }

  function close() {
    blurInside();
    emit('input', false);
  }

  // Before the DOM changes: remember the opener, or let go of the focus before hiding.
  watch(
    () => props.value,
    (open) => {
      if (props.docked) return;
      if (open) {
        const active = document.activeElement;
        opener =
          active instanceof HTMLElement && active !== document.body
            ? active
            : null;
      } else {
        blurInside();
      }
    }
  );

  // After the DOM changed: move the focus in, or give it back to the opener.
  // A parent re-render that flips `value` queues this post watcher by id,
  // before this component re-renders (Vue 2.7 scheduler): the panel would
  // still be hidden, so the focus waits for the next tick.
  watch(
    () => props.value,
    (open) => {
      if (props.docked) return;
      void nextTick(() => {
        if (props.docked || props.value !== open) return;
        if (open) {
          const target = props.initialFocus
            ? panel.value?.querySelector<HTMLElement>(props.initialFocus)
            : null;
          (target ?? panel.value)?.focus({ preventScroll: true });
          if (target instanceof HTMLInputElement) target.select();
          return;
        }
        const target = opener;
        opener = null;
        if (target && target.isConnected && isVisible(target))
          target.focus({ preventScroll: true });
      });
    },
    { flush: 'post' }
  );

  // Content swapped while open: its first field (or the panel) takes the focus.
  watch(
    () => props.focusKey,
    () => {
      if (props.docked || !props.value) return;
      void nextTick(() => {
        if (props.docked || !props.value) return;
        const target = props.initialFocus
          ? panel.value?.querySelector<HTMLElement>(props.initialFocus)
          : null;
        (target ?? panel.value)?.focus({ preventScroll: true });
        if (target instanceof HTMLInputElement) target.select();
      });
    },
    { flush: 'post' }
  );

  // Docked → sheet with the focus inside (a tier change): open the sheet rather than hide the focused field.
  watch(
    () => props.docked,
    (docked) => {
      cancelDrag();
      if (!docked && !props.value && focusInside()) emit('input', true);
    }
  );
  //#endregion

  //#region Drag
  function onPointerDown(event: PointerEvent) {
    if (props.docked || !panel.value) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startPx: panel.value.getBoundingClientRect().height,
      moved: false,
    };
    const handle = event.currentTarget;
    if (handle instanceof HTMLElement)
      handle.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const delta = drag.startY - event.clientY;
    if (!drag.moved && Math.abs(delta) < SHEET_DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    dragPx.value = clampSheetDrag(drag.startPx + delta, stableHeight.value);
  }

  function onPointerUp(event: PointerEvent) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const moved = drag.moved;
    const px = dragPx.value;
    cancelDrag();
    if (!moved || px === null) return;
    const next = snapSheetHeight(px, stableHeight.value);
    if (next === null) close();
    else if (next !== props.height) emit('update:height', next);
  }

  function cancelDrag() {
    drag = null;
    dragPx.value = null;
  }
  //#endregion

  onBeforeUnmount(() => {
    drag = null;
    opener = null;
  });
</script>

<template>
  <div class="pos-ui-sheet" :class="docked ? 'is-docked' : 'is-sheet'">
    <div
      v-show="!docked && value"
      class="sheet-scrim"
      aria-hidden="true"
      @click="close"
    />
    <div
      v-show="shown"
      ref="panel"
      class="sheet-panel"
      :class="{ 'is-dragging': dragPx !== null }"
      :style="panelStyle"
      :role="isDialog ? 'dialog' : undefined"
      :aria-modal="isDialog ? 'true' : undefined"
      :aria-label="isDialog ? title : undefined"
      :tabindex="docked ? undefined : -1"
      @keydown="onKeydown"
    >
      <div v-show="!docked" class="sheet-top">
        <div
          class="sheet-handle"
          aria-hidden="true"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="cancelDrag"
        >
          <span class="handle-bar" />
        </div>
        <button
          type="button"
          class="sheet-close"
          :aria-label="$t('posV2.close')"
          @click="close"
        >
          <PosUiIcon name="close" :size="20" />
        </button>
      </div>
      <div class="sheet-body">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-ui-sheet {
    // In place: the panel fills the pane it sits in.
    &.is-docked {
      @apply tw-flex tw-min-h-0 tw-min-w-0 tw-flex-col;
      .sheet-panel {
        @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;
      }
    }

    // Fixed to the bottom of the screen; the root itself takes no room.
    &.is-sheet {
      // Between the permanent drawer and the docked «Asistente» panel while
      // it is docked (the whole width otherwise).
      .sheet-scrim {
        @apply tw-fixed tw-bottom-0 tw-top-0 tw-z-40 tw-bg-scrim/40;
        left: var(--assistant-inset-left, 0px);
        right: var(--assistant-inset-right, 0px);
      }
      .sheet-panel {
        @apply tw-fixed tw-bottom-0 tw-z-50 tw-flex tw-flex-col tw-overflow-hidden tw-rounded-t-2xl tw-bg-surface tw-shadow-lg tw-outline-none;
        left: var(--assistant-inset-left, 0px);
        right: var(--assistant-inset-right, 0px);
        // Default until the stable height is known; never taller than the screen.
        height: 60%;
        max-height: calc(100% - 8px);
        padding-bottom: env(safe-area-inset-bottom);
        transition: height 180ms ease;
        animation: sheet-in 180ms ease-out;
        &.is-dragging {
          transition: none;
        }
      }
    }

    // The grab handle across the whole row; «Cerrar» sits over its right end.
    .sheet-top {
      @apply tw-relative tw-flex tw-h-11 tw-shrink-0;
    }
    .sheet-handle {
      @apply tw-flex tw-flex-1 tw-cursor-grab tw-items-center tw-justify-center;
      touch-action: none;
      .handle-bar {
        @apply tw-h-1 tw-w-10 tw-rounded-full tw-bg-neutral-300;
      }
    }
    .sheet-close {
      @apply tw-absolute tw-right-1 tw-top-0 tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-bg-neutral-100 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .sheet-body {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;
    }
  }

  @keyframes sheet-in {
    from {
      transform: translateY(24px);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .pos-ui-sheet.is-sheet .sheet-panel {
      transition: none;
      animation: none;
    }
  }
</style>
